// src/hooks/klines/adapters/binance.js
const DEFAULT_PROXY = "";

// REST bases
const REST_SPOT_BASE    = "https://api.binance.com";
const REST_FUTURES_BASE = "https://fapi.binance.com"; // USDⓈ-M futures

// WS bases
const WS_SPOT_BASE    = "wss://stream.binance.com:9443/ws";
const WS_FUTURES_BASE = "wss://fstream.binance.com/ws";

// ——— helpers
const normSym = (s) => String(s || "").replace(/[-_]/g, "").toUpperCase();
const toCandle = (t, o, h, l, c) => ({
  time:  Number(t),
  open:  Number(o),
  high:  Number(h),
  low:   Number(l),
  close: Number(c),
});

// ===== Интервалы
const SPOT_IV = new Set([
  "1s","1m","3m","5m","15m","30m","1h","2h","4h","6h","8h","12h","1d","3d","1w","1M"
]);
const FUT_IV = new Set([
  "1m","3m","5m","15m","30m","1h","2h","4h","6h","8h","12h","1d","3d","1w","1M"
]);
const mapSpotIv = (i) => (SPOT_IV.has(i) ? i : "1m");
const mapFutIv  = (i) => (FUT_IV.has(i)  ? i : "1m");

/* ===================== SPOT: REST (history) ===================== */
export async function binanceSpotFetchHistory({
  symbol,
  interval = "1m",
  limit = 500,
  proxy = DEFAULT_PROXY,
} = {}) {
  const sym = normSym(symbol);
  const iv = mapSpotIv(interval);
  const restUrl  = `${REST_SPOT_BASE}/api/v3/klines?symbol=${sym}&interval=${iv}&limit=${limit}`;
  const finalUrl = proxy ? `${proxy}/${restUrl}` : restUrl;

  const res = await fetch(finalUrl);
  if (!res.ok) throw new Error(`Binance SPOT klines HTTP ${res.status}`);
  const arr = await res.json();

  // [ openTime, open, high, low, close, ... ]
  return (arr || []).map(k => toCandle(k[0], k[1], k[2], k[3], k[4]));
}

/* ===================== SPOT: WS (live) ===================== */
// onCandle({time, open, high, low, close})
export function binanceSpotOpenWS({ symbol, interval = "1m", onCandle } = {}) {
  const sym = normSym(symbol);
  const iv  = mapSpotIv(interval);
  const url = `${WS_SPOT_BASE}/${sym.toLowerCase()}@kline_${iv}`;

  const ws = new WebSocket(url);

  ws.onmessage = (ev) => {
    try {
      const m = JSON.parse(ev.data);
      if (m?.e !== "kline" || !m?.k) return;
      const k = m.k;
      onCandle && onCandle(toCandle(k.t, k.o, k.h, k.l, k.c));
    } catch {}
  };

  return () => { try { ws.close(); } catch {} };
}

/* ===================== FUTURES: REST (history) ===================== */
export async function binanceFuturesFetchHistory({
  symbol,
  interval = "1m",
  limit = 500,
  proxy = DEFAULT_PROXY,
} = {}) {
  const sym = normSym(symbol);
  const iv  = mapFutIv(interval);
  const restUrl  = `${REST_FUTURES_BASE}/fapi/v1/klines?symbol=${sym}&interval=${iv}&limit=${limit}`;
  const finalUrl = proxy ? `${proxy}/${restUrl}` : restUrl;

  const res = await fetch(finalUrl);
  if (!res.ok) throw new Error(`Binance FUTURES klines HTTP ${res.status}`);
  const arr = await res.json();

  // [ openTime, open, high, low, close, ... ]
  return (arr || []).map(k => toCandle(k[0], k[1], k[2], k[3], k[4]));
}

/* ===================== FUTURES: WS (live) ===================== */
// onCandle({time, open, high, low, close})
export function binanceFuturesOpenWS({ symbol, interval = "1m", onCandle } = {}) {
  const sym = normSym(symbol);
  const iv  = mapFutIv(interval);
  const url = `${WS_FUTURES_BASE}/${sym.toLowerCase()}@kline_${iv}`;

  const ws = new WebSocket(url);

  ws.onmessage = (ev) => {
    try {
      const m = JSON.parse(ev.data);
      if (m?.e !== "kline" || !m?.k) return;
      const k = m.k;
      onCandle && onCandle(toCandle(k.t, k.o, k.h, k.l, k.c));
    } catch {}
  };

  return () => { try { ws.close(); } catch {} };
}
